import { Info, Loader2, Repeat } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { MESI, slug } from "@/lib/format";

export const PageHeader = ({ overline, title, subtitle, children }) => (
  <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-8 fade-up">
    <div>
      {overline && <div className="overline-label mb-2">{overline}</div>}
      <h1 className="text-3xl sm:text-4xl font-medium text-[#2C2623]" data-testid="page-title">{title}</h1>
      {subtitle && <p className="text-sm text-[#756B64] mt-2 max-w-xl leading-relaxed">{subtitle}</p>}
    </div>
    {children && <div className="flex flex-wrap items-center gap-2">{children}</div>}
  </div>
);

export const EmptyState = ({ icon: Icon, title, text, action, testId = "empty-state" }) => (
  <div data-testid={testId} className="card-soft py-16 px-6 flex flex-col items-center text-center">
    <span className="h-14 w-14 rounded-full bg-[#F4EFEA] grid place-items-center mb-4">
      <Icon className="h-6 w-6 text-[#8C6D62]" strokeWidth={1.6} />
    </span>
    <h3 className="text-lg font-medium">{title}</h3>
    {text && <p className="text-sm text-[#756B64] mt-1 max-w-sm">{text}</p>}
    {action && <div className="mt-5">{action}</div>}
  </div>
);

export const Hint = ({ children, testId = "hint" }) => (
  <div data-testid={testId} className="flex gap-3 items-start rounded-2xl bg-[#E8C5C8]/25 border border-[#E8C5C8]/60 px-4 py-3 text-sm text-[#5E4A42]">
    <Info className="h-4 w-4 mt-0.5 shrink-0 text-[#8C6D62]" />
    <div className="leading-relaxed">{children}</div>
  </div>
);

export const Loading = () => (
  <div className="py-24 grid place-items-center" data-testid="loading">
    <Loader2 className="h-6 w-6 animate-spin text-[#8C6D62]" />
  </div>
);

const CH_STYLE = {
  "E-commerce": "bg-[#8AA090]/15 text-[#4E6656] border-[#8AA090]/30",
  "Rete fisica": "bg-[#D98A72]/15 text-[#9A5440] border-[#D98A72]/30",
  Generale: "bg-[#8C6D62]/10 text-[#6B5249] border-[#8C6D62]/20",
};

export const ChannelBadge = ({ canale }) => (
  <span className={`inline-flex items-center text-[11px] font-medium px-2 py-0.5 rounded-full border whitespace-nowrap ${CH_STYLE[canale]}`}>{canale}</span>
);

export const TipoBadge = ({ tipo, ricorrente }) => (
  <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${tipo === "incasso" ? "bg-[#8AA090]/20 text-[#4E6656]" : "bg-[#E07A5F]/15 text-[#A2513C]"}`}>
    {ricorrente && <Repeat className="h-3 w-3" />}
    {tipo === "incasso" ? "Incasso" : "Costo"}
  </span>
);

export const SimpleSelect = ({ value, onChange, options, placeholder, testId, className = "" }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testId} className={`h-10 bg-white rounded-xl border-[#EAE3DC] ${className}`}>
      <SelectValue placeholder={placeholder} />
    </SelectTrigger>
    <SelectContent>
      {options.map((o) => (
        <SelectItem key={o.value} value={String(o.value)} data-testid={`${testId}-option-${slug(o.value)}`}>
          {o.label}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

const YEARS = Array.from({ length: new Date().getFullYear() - 2022 }, (_, i) => 2024 + i);

export const PeriodSelect = ({ anno, mese, onChange, allowYear = false, prefix = "filter" }) => (
  <>
    <SimpleSelect
      testId={`${prefix}-month-select`}
      className="w-40"
      value={mese == null ? "all" : String(mese)}
      onChange={(v) => onChange({ anno, mese: v === "all" ? null : Number(v) })}
      options={[...(allowYear ? [{ value: "all", label: "Tutto l'anno" }] : []), ...MESI.map((n, i) => ({ value: String(i + 1), label: n }))]}
    />
    <SimpleSelect
      testId={`${prefix}-year-select`}
      className="w-28"
      value={String(anno)}
      onChange={(v) => onChange({ anno: Number(v), mese })}
      options={YEARS.map((y) => ({ value: String(y), label: String(y) }))}
    />
  </>
);

export const ConfirmDialog = ({ open, onOpenChange, title, text, onConfirm, confirmLabel = "Elimina" }) => (
  <AlertDialog open={open} onOpenChange={onOpenChange}>
    <AlertDialogContent data-testid="confirm-dialog" className="rounded-2xl">
      <AlertDialogHeader>
        <AlertDialogTitle className="font-display font-medium">{title}</AlertDialogTitle>
        <AlertDialogDescription>{text}</AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel data-testid="confirm-cancel-button" className="rounded-full">Annulla</AlertDialogCancel>
        <AlertDialogAction data-testid="confirm-ok-button" onClick={onConfirm} className="rounded-full bg-[#C0573F] hover:bg-[#A84A35]">
          {confirmLabel}
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

export const Field = ({ label, children, hint }) => (
  <label className="block space-y-1.5">
    <span className="text-xs font-semibold text-[#5E4A42]">{label}</span>
    {children}
    {hint && <span className="block text-[11px] text-[#756B64]">{hint}</span>}
  </label>
);

export const btnPrimary = "rounded-full bg-[#8C6D62] hover:bg-[#77594E] text-white px-5 h-10 transition-colors";
export const btnGhost = "rounded-full border-[#EAE3DC] bg-white hover:bg-[#F4EFEA] h-10";
export const inputCls = "h-10 rounded-xl bg-white border-[#EAE3DC] focus-visible:ring-[#8C6D62]/40";
